<template>
  <div class="patient-list">
    <div class="mt-3 mb-0 w-100 d-flex flex-row justify-content-between">
      <div class="d-flex align-items-center">
        <div class="">
          <a
            v-if="hasCurrentUserReportPermission('Patients')"
            class="hover-pointer"
            style="font-size: 14px"
            @click="showReportRequestsModal = true"
          >
            <i class="fa fa-download"></i>
            <span class="ml-2 text-primary" style="font-size: 14px"
              >Export</span
            >
          </a>
        </div>
        <div
          v-if="
            currentUser &&
              !currentUser.onlyAccessAssignedPatients &&
              hasCurrentUserReportPermission('Patients')
          "
          class="border-left border-med-light-gray pl-1 ml-1 mr-1"
          style="width: 1px; height: 19px"
        ></div>
        <vue-toggles
          v-if="currentUser && !currentUser.onlyAccessAssignedPatients"
          @click="changeAssignedToMEToggle()"
          :value="assignedToMe"
          height="20"
          width="30"
          checkedBg="#34C759"
          uncheckedBg="#FF6161"
        />
        <small
          v-if="currentUser && !currentUser.onlyAccessAssignedPatients"
          class="ml-2"
          >Show My Patients only</small
        >
      </div>
      <div class="d-flex flex-row justify-content-end align-items-center">
        <top-paginator
          :total="listTotal"
          :page="pagination.page"
          :perPage="pagination.pageSize"
          @change-page="changePage"
        />
      </div>
    </div>
    <div class="grid-container">
      <grid
        ref="grid"
        :groupFeature="false"
        :autoHeight="true"
        :columns="columns"
        :data="items"
        :store="gridConfig.store"
        :selectionMode="gridConfig.selectionMode"
        :columnLines="gridConfig.columnLines"
        :searchFeature="true"
        :stripeFeature="false"
        @storesort="gridListeners.storeSort"
        @renderrows="hookDropdownButtons"
        @renderrow="hookDropdownButtons"
      ></grid>
    </div>
    <report-request-index
      v-if="showReportRequestsModal"
      v-model="showReportRequestsModal"
      :reportFilters="exportFilter"
      reportName="Patients"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import EocView from "./EocView.vue";
import { formatDateOnly, formatDate } from "../../helpers/date";
import Grid from "../../third_party/grid/Grid.vue";
import TopPaginator from "../controls/TopPaginator.vue";
import ReportRequestIndex from "../report_request/ReportRequestIndex.vue";

export default {
  name: "EocsListView",
  components: { Grid, TopPaginator, ReportRequestIndex },
  props: [
    "showing",
    "list",
    "listTotal",
    "exportFilter",
    "orderBy",
    "orderByDescending",
    "assignedToMeValue"
  ],
  data() {
    const self = this;
    return {
      pagination: {
        page: 0,
        pageSize: 50
      },
      gridConfig: {
        minHeight: "20em",
        selectionMode: {
          row: false,
          checkbox: false,
          rowCheckboxSelection: false,
          showCheckAll: false
        },
        columnLines: false,
        store: {
          sorters: [
            {
              field: this.orderBy,
              ascending: !this.orderByDescending
            }
          ],
          remoteSort: true
        }
      },
      gridListeners: {
        storeSort: sortData => {
          this.store = sortData.source;
          if (sortData.sorters.length == 1) {
            const sorter = sortData.sorters[0];
            const ascending = sorter.ascending;
            const field = sorter.field;
            self.$emit("change-order", {
              orderBy: field,
              orderByDescending: !ascending
            });
          }
        }
      },
      lastTimeout: null,
      refreshClients: [],
      refreshEocs: [],
      firstShowing: true,
      eocStatuses: [
        {
          value: "Active",
          label: "Active",
          itemClass: "badge-v2 badge-success"
        },
        {
          value: "Pending",
          label: "Pending",
          itemClass: "badge-v2 badge-warning"
        },
        {
          value: "Inactive",
          label: "Inactive",
          itemClass: "badge-v2 badge-danger"
        }
      ],
      store: null,
      showReportRequestsModal: false
    };
  },
  mounted() {},
  methods: {
    viewClientEOC(eoc) {
      this.$emit("add-tab", {
        key: "eoc-" + eoc.id,
        text: this.formatEocObj(eoc),
        component: EocView,
        props: {
          eocId: eoc.id,
          clientId: eoc.patient.id
        },
        closeable: true
      });
    },
    changePage(page) {
      if (page * this.pagination.pageSize + 1 > this.list.length) {
        this.$emit("load-more");
      }
      this.pagination = {
        ...this.pagination,
        page
      };
    },
    changeAssignedToMEToggle() {
      this.assignedToMe = !this.assignedToMe;
    }
  },
  computed: {
    ...mapGetters("client", ["listeners"]),
    ...mapGetters("layout", ["hasCurrentUserReportPermission"]),
    ...mapState("layout", {
      currentUser: state => state.currentUser,
      selectedLocation: state => state.selectedLocation
    }),
    items() {
      const statusValue = status => {
        if (status == "Pending") return 0;
        if (status == "Active") return 1;
        return 2;
      };
      return this.list
        .slice(
          this.pagination.page * this.pagination.pageSize,
          (this.pagination.page + 1) * this.pagination.pageSize
        )
        .map(item => {
          return {
            ...item,
            statusLabel: item.status,
            status: statusValue(item.status)
          };
        });
    },
    columns() {
      return [
        {
          text: "Name",
          field: "name",
          type: "template",
          flex: 1,
          editor: false,
          template: ({ record }) => {
            return this.formatClient(
              record.data.patient.firstName,
              record.data.patient.middleName,
              record.data.patient.preferredName,
              record.data.patient.lastName
            );
          }
        },
        {
          text: "DOB",
          field: "dateOfBirth",
          type: "template",
          width: 90,
          editor: false,
          template: data => formatDateOnly(data.record.data.patient.dateOfBirth)
        },
        {
          text: "Mrn",
          field: "mrn",
          type: "template",
          flex: 1,
          editor: false,
          template: data =>
            this.formatMrn(data.record.data.patient.mrn, data.record.data.eocn)
        },
        {
          text: "Status",
          field: "status",
          type: "template",
          width: 140,
          editor: false,
          template: ({ record }) =>
            `<span id="eocsListView-status"  class="badge-fixed-size px-4 py-2 ${
              this.eocStatuses.find(s => s.value == record.statusLabel)
                .itemClass
            }">${record.statusLabel}</span>`
        },
        {
          text: "Current LOC",
          field: "levelOfCare",
          type: "template",
          flex: 1,
          editor: false,
          template: data =>
            `<span id="eocsListView-loc"
          >${
            data.record.data.levelOfCare
              ? data.record.data.levelOfCare.name
              : ""
          }</span>`
        },
        {
          text: "Start/Admission date",
          field: "startDate",
          type: "template",
          width: 160,
          editor: false,
          template: data =>
            `<span id="eocsListView-startDate"
          >${
            data.record.data.startDate
              ? formatDate(data.record.data.startDate)
              : ""
          }</span>`
        },
        {
          text: "End date",
          field: "endDate",
          type: "template",
          width: 160,
          editor: false,
          template: data =>
            `<span id="eocsListView-endDate"
          >${
            data.record.data.endDate
              ? formatDateOnly(data.record.data.endDate)
              : ""
          }</span>`
        },
        {
          text: "Badges & Alerts",
          field: "alerts",
          type: "template",
          flex: 1,
          editor: false,
          template: data =>
            `<div class="d-flex flex-wrap">
            ${data.record.data.badges
              .map(
                badge => `
            <div id="eocIndexList-badge-value_${badge.acronym}"
              class="badge text-white mr-1 mb-1"
              style="background-color: ${badge.category.color};}"
              data-toggle="tooltip" data-placement="top" title="${badge.name}"
            >
              ${badge.acronym}
            </div>
            `
              )
              .join("")}
            ${
              data.record.data.notes &&
              data.record.data.notes.filter(n => n.status == "Enabled").length >
                0
                ? `<div id="eocIndexList-alert-value" class="little-circle-exclamation-container">
                <i class="alert-note-icon fas fa-exclamation-circle text-danger"
                ></i>
            </div>
            `
                : ""
            }
          </div>`
        },
        {
          text: "Discharge reason",
          field: "dischargeReason",
          type: "template",
          flex: 1,
          editor: false,
          template: data =>
            `<span id="eocsListView-dischargeReason">
             ${
               data.record.data.dischargeReason?.name.toLowerCase() == "other"
                 ? data.record.data.otherDischargeReason
                 : data.record.data.dischargeReason?.name != null
                 ? data.record.data.dischargeReason.name
                 : ""
             }</span>`
        },
        {
          text: "Location",
          field: "location",
          type: "template",
          flex: 1,
          editor: false,
          template: data =>
            `<span id="eocsListView-location">
             ${
               data.record.data.location ? data.record.data.location.name : ""
             }</span>`
        },
        {
          text: "Place of Service",
          field: "placeOfService",
          type: "template",
          flex: 4,
          editor: false,
          sortable: false,
          autoHeight: true,
          template: data =>
            data.record.data.placesOfService?.length > 0
              ? `<div class="text-wrap" style="height: 100%; padding: 10px 5px; display: flex; align-items: center;">
                  <div style="max-height: 100%;">
                    ${data.record.data.placesOfService
                      .map(place => place.code + "-" + place.name)
                      .join(",")}
                  </div>
                </div>`
              : ""
        },
        {
          text: "Tools",
          type: "action",
          cellCls: "pointer-cell",
          field: "action",
          align: "right",
          width: 80,
          actions: [
            {
              tooltip: "View",
              renderer: () =>
                `<i class="fa fa-search-plus color-custom-dark-grey hover-text-primary b-action-item grid-action-icon"></i>`,
              onClick: ({ record }) => {
                this.viewClientEOC(record.data);
              }
            }
          ]
        }
      ].filter(c => !this.selectedLocation || c.field != "location");
    },
    assignedToMe: {
      get() {
        return this.assignedToMeValue;
      },
      set() {
        this.$emit("change-assignedToMe");
      }
    }
  },

  watch: {
    orderBy(newValue) {
      this.$refs["grid"].gridInstance._store.sort({
        field: newValue,
        ascending: !this.orderByDescending
      });
    },
    orderByDescending(newValue) {
      this.$refs["grid"].gridInstance._store.sort({
        field: this.orderBy,
        ascending: !newValue
      });
    },
    showing() {
      this.$refs["grid"].gridInstance._store.sort({
        field: this.orderBy,
        ascending: !this.orderByDescending
      });
    }
  }
};
</script>

<style scoped>
.filter-select {
  height: 50px;
  width: 300px;
  padding-top: 5px;
}
.search-input {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  min-width: 250px;
}
.client-card {
  min-width: 180px;
  max-width: 180px;
  height: 315px;
}
.little-circle {
  width: 10px;
  height: 10px;
}
.little-circle-exclamation {
  width: 20px;
  height: 20px;
}
.little-circle-exclamation-container {
  width: 15px;
  height: 15px;
  margin-right: 5px;
}
.client-avatar {
  width: 100px;
  height: 100px;
  text-align: center;
}
.client-data {
  font-size: 0.8rem;
  word-break: break-all;
  overflow: hidden;
  text-overflow: clip;
}
.client-card:hover {
  cursor: pointer;
  box-shadow: 0 10px 10px -10px rgba(0, 0, 0, 0.5);
  transform: translateY(-3px);
  transition-duration: 0.3s;
  transition-property: transform;
}
.clients-list {
  height: calc(100vh - 264px - var(--filter-height));
  overflow-y: auto;
}
.border-yellow {
  border-color: #ffc107 !important;
}
.bg-yellow {
  background-color: #ffc107 !important;
}
.client-text-data {
  height: 180px;
}
.client-eoc-data {
  height: 119px;
}
.filter-values {
  width: 250px;
}
.filter-value {
  position: relative;
  min-width: 100px;
  color: #a6a6a6;
  font-size: 14px;
}
.filter-value-delete {
  position: absolute;
  top: -8px;
  right: -8px;
}
.filter-value-delete:hover {
  cursor: pointer;
}
.clear-all-filters:hover {
  cursor: pointer;
}
.filter-values {
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
}
.filters-container {
  overflow-x: auto;
}
.separator-or {
  margin-bottom: -13px;
}
div.disabled {
  pointer-events: none;

  /* for "disabled" effect */
  opacity: 0.5;
  background: #ccc;
}
.max-2-lines {
  max-height: 38px;
}
.tooltip-icon-bg {
  top: 5px;
  left: 5px;
  width: 10px;
  height: 13px;
  z-index: 1;
}
.patient-list >>> .b-grid-container .b-container {
  max-height: calc(100vh - 290px - var(--filter-height));
}
</style>

<style>
.filter-select .vs__dropdown-toggle {
  height: 100%;
  border-radius: 0;
  border: 0;
}

.patient-badge {
  color: white;
  font-size: 11px;
  width: 40px;
  height: 16px;
  text-align: center;
  border-radius: 25px;
  line-height: 1.5;
}
.patient-picker-hide-options .vs__dropdown-menu {
  display: none;
}
.patient-picker-hide-options.vs--open .vs__dropdown-toggle {
  border-bottom-color: #4044e6 !important;
  border-bottom-width: 2px;
}
.patient-list .grid-container .b-grid-container {
  display: flex;
  width: 100%;
  height: 100% !important;
  flex: 1;
}
.patient-list .grid-container .b-grid {
  max-height: 100%;
  width: 100%;
}
</style>
